import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { CurrentUserService } from '@/app/core/current-user/current-user.service';
import { PersonaService } from '@/app/core/current-user/persona.service';
import { ScopeService } from '@/app/core/scope/scope.service';

/**
 * Ensures the signed-in actor + permissions are loaded before a guarded route
 * renders. Runs after `autoLoginPartialRoutesGuard` has confirmed authentication,
 * so a token is available for the `Me` query. A load failure does not block the
 * shell — permission-gated actions simply stay hidden until it succeeds.
 *
 * Once the actor is loaded, the active tenant scope is derived from it (operator — a global super-admin or any
 * holder of a global flag — → all; a tenant administrator → its administered tenants) so every screen reads scope
 * from one source.
 *
 * It also resolves the persona (T1-07) — for a non-operator that is the one read of its server portfolio, which the
 * capability cache keeps for the session — so the shell's first render already shows the persona's menu instead of
 * switching under the user's eyes. That read never rejects.
 *
 * The shell runs it as `canActivateChild` too, i.e. on EVERY navigation inside it: what an earlier navigation failed to
 * read (`Me`, the portfolio) is asked again, so a persona left `unresolved` by a passing failure is decided on the next
 * click — not only after a reload. A cached read costs nothing, and a failed one is asked once per navigation
 * (`CurrentUserService.ensureLoaded`).
 */
export const currentUserGuard: CanActivateFn = async () => {
    const currentUser = inject(CurrentUserService);
    const scope = inject(ScopeService);
    const persona = inject(PersonaService);
    try {
        await currentUser.ensureLoaded();
    } catch {
        // Render the shell anyway: gated actions stay hidden and the persona `unresolved` (the home route's notice).
        return true;
    }
    // Derived once per loaded actor: running again on every navigation hands the lists that read the scope no new value.
    scope.initializeFromActor();
    await persona.ensureResolved();
    return true;
};
